import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { type SupportedLanguage } from '@/i18n'
import { localizedRoutes, findRouteKeyFromPath } from '@/i18n/routes'
import { cn } from '@/lib/utils'

const languages = [
  { code: 'it' as const, flag: '🇮🇹', name: 'Italiano', short: 'IT' },
  { code: 'en' as const, flag: '🇬🇧', name: 'English', short: 'EN' },
]

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
  isScrolled?: boolean
}

export function LanguageSelector({
  variant = 'desktop',
  isScrolled = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage = languages.find((l) => l.code === currentLang) || languages[0]

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) {
      setIsOpen(false)
      return
    }

    const pathSegments = location.pathname.split('/').filter(Boolean)
    const pathWithoutLang = pathSegments.slice(1)

    if (pathWithoutLang.length === 0 || !pathWithoutLang[0]) {
      i18n.changeLanguage(newLang)
      navigate(`/${newLang}`)
      setIsOpen(false)
      return
    }

    const currentPathSegment = pathWithoutLang[0]
    const routeKey = findRouteKeyFromPath(currentPathSegment, currentLang)

    let newPath: string
    if (routeKey) {
      newPath = localizedRoutes[routeKey][newLang]
    } else {
      newPath = currentPathSegment
    }

    i18n.changeLanguage(newLang)
    navigate(`/${newLang}${newPath ? `/${newPath}` : ''}`)
    setIsOpen(false)
  }

  if (variant === 'mobile') {
    return (
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => switchLanguage(lang.code)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              lang.code === currentLang
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            <span className="text-base">{lang.flag}</span>
            {lang.name}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
          isScrolled
            ? 'text-gray-700 hover:bg-gray-100'
            : 'text-white/90 hover:bg-white/10'
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-base leading-none">{currentLanguage.flag}</span>
        <span>{currentLanguage.short}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50"
          role="listbox"
          aria-activedescendant={`lang-${currentLang}`}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              id={`lang-${lang.code}`}
              role="option"
              aria-selected={lang.code === currentLang}
              onClick={() => switchLanguage(lang.code)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left',
                lang.code === currentLang
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
